import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, ImagePlus, Loader2, X } from "lucide-react";
import {
  useGetSettings,
  useUpdateSettings,
  getGetSettingsQueryKey,
  getListInternalCatalogQueryKey,
  type UpdateSettingsBody,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useSetupGuide } from "@/hooks/use-setup-guide";
import { useToast } from "@/hooks/use-toast";
import { emitTourEvent } from "@/lib/tour-events";
import { industryPages } from "@/data/industry-pages";
import { COST_PRESETS } from "@/data/cost-presets";

const Z = 8500;
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/** Scale a logo to at most 600 px wide so PDFs and emails stay small. */
function resizeLogo(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.onloadend = () => {
      const original = reader.result as string;
      const img = new Image();
      img.onerror = () => reject(new Error("That file isn't an image we can use."));
      img.onload = () => {
        const MAX_W = 600;
        const scale = img.naturalWidth > MAX_W ? MAX_W / img.naturalWidth : 1;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(original); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png", 0.92));
      };
      img.src = original;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * First-login welcome form: the few business details that appear on every
 * quote, plus the user's trade so their cost catalogue starts filled in. It
 * saves the settings itself, so there is no tour step that has to notice a
 * save. Finishing or skipping it opens the setup checklist.
 */
export function WelcomeSetup() {
  const { guideState, finishWelcome } = useSetupGuide();
  const { user } = useAuth();

  const shouldShow =
    !!guideState &&
    !guideState.welcomeDone &&
    !guideState.dismissed &&
    !guideState.tourActive &&
    !guideState.completedSteps.includes("business-details");

  if (!shouldShow || !user) return null;
  return <WelcomeForm defaultEmail={user.email} onDone={finishWelcome} />;
}

function WelcomeForm({ defaultEmail, onDone }: { defaultEmail: string; onDone: () => Promise<void> }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useGetSettings({ query: { queryKey: getGetSettingsQueryKey() } });
  const updateSettings = useUpdateSettings();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [companyName, setCompanyName] = useState("");
  const [trade, setTrade] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);
  const [busy, setBusy] = useState<"save" | "skip" | null>(null);

  const trades = useMemo(
    () => industryPages.filter((ind) => COST_PRESETS[ind.slug]).map((ind) => ({ slug: ind.slug, name: ind.name })),
    [],
  );

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    setLogoError(null);
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("Image exceeds 2 MB. Please choose a smaller file.");
      return;
    }
    try {
      setLogo(await resizeLogo(file));
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : "Could not use that image.");
    }
  };

  const importTradeCosts = async (slug: string) => {
    const items = COST_PRESETS[slug];
    if (!items) return;
    const rows = items.map((item) => ({
      name: item.name,
      description: item.description,
      unitCost: item.unitCost,
      unit: item.unit,
      category: item.category,
      type: item.type,
    }));
    const res = await fetch(`${BASE}/api/internal-catalog/import`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rows, source: "preset" }),
    });
    if (!res.ok) throw new Error(`Server error ${res.status}`);
    void queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
  };

  const save = async () => {
    if (!companyName.trim()) {
      setNameError(true);
      return;
    }
    setBusy("save");
    try {
      // PUT /settings replaces the whole row, so start from what's saved.
      // A brand-new account has no row yet; give it the same defaults the
      // Settings page would.
      const current = (settings ?? {}) as UpdateSettingsBody & { id?: number };
      const isNewRow = current.id == null;
      const payload: UpdateSettingsBody = {
        ...current,
        companyName: companyName.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        logoBase64: logo ?? (isNewRow ? null : current.logoBase64 ?? null),
        accentColor: current.accentColor ?? "#1e3a5f",
        documentTitle: current.documentTitle ?? "QUOTATION",
        currencyLabel: current.currencyLabel ?? "USD",
        invoiceDocumentTitle: current.invoiceDocumentTitle ?? "INVOICE",
      };
      await updateSettings.mutateAsync({ data: payload });
      void queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
      emitTourEvent("settings.saved");

      if (trade) {
        try {
          await importTradeCosts(trade);
        } catch {
          toast({
            title: "Your details are saved",
            description: "We couldn't load the starter costs for your trade. You can import them from the Cost Catalog.",
          });
        }
      }

      await onDone();
    } catch {
      toast({ title: "Couldn't save your details", description: "Please try again.", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const skip = async () => {
    setBusy("skip");
    try {
      await onDone();
    } finally {
      setBusy(null);
    }
  };

  const disabled = busy !== null || isLoading;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: Z,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(15,23,42,0.5)", padding: "1rem",
      }}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); void save(); }}
        style={{
          width: "32rem", maxWidth: "100%", maxHeight: "calc(100vh - 2rem)", overflowY: "auto",
          background: "hsl(var(--card))", color: "hsl(var(--foreground))",
          borderRadius: "1rem", border: "1px solid hsl(var(--border))",
          boxShadow: "0 28px 70px -14px rgba(0,0,0,0.5)", padding: "1.75rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <Building2 style={{ width: "1.1rem", height: "1.1rem", color: "hsl(var(--primary))" }} />
          <span style={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "hsl(var(--muted-foreground))" }}>
            Welcome to QuoTrak
          </span>
        </div>
        <h2 style={{ fontSize: "1.4rem", fontWeight: 700, margin: "0 0 0.4rem" }}>
          Tell us about your business
        </h2>
        <p style={{ fontSize: "0.875rem", lineHeight: 1.5, color: "hsl(var(--muted-foreground))", margin: "0 0 1.25rem" }}>
          These details go on every quote and invoice you send. You can add your address and change anything later in Settings.
        </p>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="welcome-company">Business name</Label>
            <Input
              id="welcome-company"
              autoFocus
              value={companyName}
              onChange={(e) => { setCompanyName(e.target.value); setNameError(false); }}
              placeholder="e.g. Smith Plumbing"
              aria-invalid={nameError}
            />
            {nameError && <p className="text-xs text-destructive">Add your business name to continue.</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="welcome-trade">Your trade</Label>
            <select
              id="welcome-trade"
              value={trade}
              onChange={(e) => setTrade(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Something else</option>
              {trades.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">
              We'll fill your cost catalog with typical labor and material rates for your trade. Adjust them any time.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="welcome-phone">Phone</Label>
              <Input id="welcome-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="welcome-email">Business email</Label>
              <Input id="welcome-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Logo <span className="font-normal text-muted-foreground">(optional, a Pro feature included in your trial)</span></Label>
            <div className="flex items-center gap-3">
              {logo ? (
                <div className="relative">
                  <img src={logo} alt="Your logo" className="h-12 max-w-[9rem] object-contain rounded border bg-white p-1" />
                  <button
                    type="button"
                    onClick={() => setLogo(null)}
                    aria-label="Remove logo"
                    className="absolute -top-2 -right-2 rounded-full bg-background border p-0.5 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <ImagePlus className="w-4 h-4 mr-1.5" /> Upload logo
                </Button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogo} />
            </div>
            {logoError && <p className="text-xs text-destructive">{logoError}</p>}
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 mt-6">
          <Button type="button" variant="ghost" disabled={disabled} onClick={() => void skip()}>
            {busy === "skip" ? "One moment…" : "Skip for now"}
          </Button>
          <Button type="submit" disabled={disabled}>
            {busy === "save" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : "Save and continue"}
          </Button>
        </div>
      </form>
    </div>
  );
}
